<template>
	<a-modal
		:visible="visible"
		:footer="null"
		:closable="false"
		:mask-closable="false"
		:destroy-on-close="true"
		width="860px"
		:centered="true"
		wrap-class-name="g-image-settings-modal"
		@cancel="onCancel"
	>
		<div class="g-edit-modal__scroll">
		<div class="g-image-settings">
			<div class="g-image-settings__header">
				<div class="g-image-settings__title-row">
					<span class="g-image-settings__title">图片设置</span>
					<span class="g-image-settings__subtitle">图片宽度建议750px以上</span>
				</div>
				<span class="g-image-settings__close mbm-iconfont mbm-guanbi1" @click="onCancel"></span>
			</div>

			<div class="g-image-settings__body">
				<div class="g-image-settings__preview-col">
					<div class="g-image-settings__section-label">效果预览</div>
					<div class="g-image-settings__phone">
						<div
							class="g-image-settings__phone-screen"
							:class="[`is-style-${form.imageStyle}`, `is-display-${form.displayMode}`]"
						>
							<HeaderImagesPreview
								v-if="previewImages.length"
								:items="previewImages"
								:image-style="form.imageStyle"
								:display-mode="form.displayMode"
							/>
							<div v-else class="g-image-settings__phone-empty">暂无图片</div>
						</div>
					</div>
				</div>

				<div class="g-image-settings__config-col">
					<UploadProgressPanel
						v-if="showFullUploadPanel"
						:tasks="uploadTasks"
						:merge="uploadTasks.length > 1"
						variant="modal"
					/>
			<EditListSortGroup
				v-else
				ref="sortList"
				class="g-image-settings__upload-list"
				@drop.prevent="onSortDrop"
			>
						<div
							v-for="(img, index) in form.items"
							:key="img.id"
							:data-sort-id="img.id"
							class="g-image-settings__thumb"
							:class="[
								{ 'is-uploading': isActiveUploadItem(img, index) },
								dragId === img.id ? 'is-list-sort-dragging' : '',
							]"
							:draggable="!img.uploading"
							@dragstart="onDragStart(img.id)"
							@dragover.prevent="onItemDragOver(img.id, $event)"
							@dragend="onDragEnd"
						>
							<UploadProgressPanel
								v-if="isActiveUploadItem(img, index)"
								:tasks="uploadTasks"
								:merge="uploadTasks.length > 1"
								variant="item"
								hide-filename
							/>
							<template v-else>
								<img :src="img.previewUrl" alt="" />
								<div class="g-image-settings__thumb-actions">
									<span
										class="g-image-settings__thumb-btn"
										title="更换"
										@click="onReplaceImage(index)"
									>⟳</span>
									<span
										class="g-image-settings__thumb-btn is-delete"
										title="删除"
										@click="onDeleteImage(index)"
									>×</span>
								</div>
							</template>
						</div>
						<div
							v-if="form.items.length < maxImages && !isUploading"
							class="g-image-settings__upload-add"
							@click="onAddImages"
						>
							<i class="mbm-iconfont mbm-tupian"></i>
							<span>上传图片</span>
						</div>
					</EditListSortGroup>

					<div v-if="!hideImageStyle" class="g-image-settings__group">
						<div class="g-image-settings__group-title">图片样式设置</div>
						<div class="g-image-settings__radio-row">
							<label
								v-for="item in imageStyleOptions"
								:key="item.value"
								class="g-image-settings__radio"
								:class="{ 'is-checked': form.imageStyle === item.value }"
							>
								<input
									type="radio"
									:value="item.value"
									v-model="form.imageStyle"
								/>
								<span class="g-image-settings__radio-dot"></span>
								<span>{{ item.label }}</span>
							</label>
						</div>
					</div>

					<div class="g-image-settings__group">
						<div class="g-image-settings__group-title">图片展示设置</div>
						<div class="g-image-settings__radio-row">
							<label
								v-for="item in displayModeOptions"
								:key="item.value"
								class="g-image-settings__radio"
								:class="{ 'is-checked': form.displayMode === item.value }"
							>
								<input
									type="radio"
									:value="item.value"
									v-model="form.displayMode"
								/>
								<span class="g-image-settings__radio-dot"></span>
								<span>{{ item.label }}</span>
							</label>
						</div>
						<p class="g-image-settings__hint">{{ displayModeHint }}</p>
					</div>
				</div>
			</div>

			<div class="g-image-settings__footer">
				<span class="g-image-settings__sort-hint">图片支持拖拽排序</span>
				<span class="g-image-settings__count">已上传 {{ previewImages.length }} 张图片</span>
				<div class="g-image-settings__footer-actions">
					<button type="button" class="g-image-settings__btn is-cancel" @click="onCancel">取消</button>
					<button
						type="button"
						class="g-image-settings__btn is-confirm"
						:disabled="!previewImages.length || isUploading"
						@click="onConfirm"
					>确认</button>
				</div>
			</div>
		</div>
		</div>

		<input
			ref="fileInput"
			type="file"
			accept="image/*"
			multiple
			class="g-image-settings__file-input"
			@change="onFileChange"
		/>
		<input
			ref="replaceInput"
			type="file"
			accept="image/*"
			class="g-image-settings__file-input"
			@change="onReplaceFileChange"
		/>
	</a-modal>
</template>

<script>
	import { v4 as uuidv4 } from 'uuid';
	import HeaderImagesPreview from './HeaderImagesPreview.vue';
	import {
		MAX_LOCAL_IMAGES,
		filterValidLocalFiles,
		normalizeHeaderImages,
		normalizeLocalImages,
		revokeLocalImageUrl,
		revokeLocalImages,
	} from '../utils/localImage.js';
	import EditListSortGroup from './EditListSortGroup.vue';
	import UploadProgressPanel from './UploadProgressPanel.vue';
	import {
		createUploadTasks,
		updateUploadTaskProgress,
		uploadHeaderMediaFiles,
	} from '../utils/uploadHeaderMedia.js';
	import {
		handleEditListSortDragEnd,
		handleEditListSortDragOver,
		handleEditListSortDragStart,
	} from '../utils/editListDragSort.js';

	const IMAGE_STYLE_OPTIONS = [
		{ value: 'margin', label: '留白' },
		{ value: 'full', label: '撑满' },
	];

	const DISPLAY_MODE_OPTIONS = [
		{ value: 'tile', label: '平铺' },
		{ value: 'carousel', label: '轮播' },
	];

	export default {
		name: 'GImageSettingsModal',
		components: { EditListSortGroup, HeaderImagesPreview, UploadProgressPanel },
		props: {
			visible: {
				type: Boolean,
				default: false,
			},
			value: {
				type: Object,
				default: () => ({
					items: [],
					imageStyle: 'full',
					displayMode: 'carousel',
				}),
			},
			/** 正文图片组件不展示留白/撑满 */
			hideImageStyle: {
				type: Boolean,
				default: false,
			},
		},
		emits: ['update:visible', 'confirm'],
		data() {
			return {
				maxImages: MAX_LOCAL_IMAGES,
				imageStyleOptions: IMAGE_STYLE_OPTIONS,
				displayModeOptions: DISPLAY_MODE_OPTIONS,
				form: {
					items: [],
					imageStyle: 'full',
					displayMode: 'carousel',
				},
				dragId: '',
				dragOverId: '',
				replaceIndex: -1,
				pendingFiles: [],
				filePickMode: 'add',
				skipNextInit: false,
				isUploading: false,
				uploadMode: 'initial',
				uploadTargetId: '',
				replaceUploadIndex: -1,
				uploadTasks: [],
			};
		},
		computed: {
			showFullUploadPanel() {
				return this.isUploading && this.uploadMode === 'initial';
			},
			previewImages() {
				return this.form.items.filter((img) => !img.uploading);
			},
			displayModeHint() {
				if (this.form.displayMode === 'tile') {
					return '建议9张以内，图片从上至下依次展示';
				}
				return '建议9张以内，图片以轮播形式切换展示';
			},
		},
		watch: {
			visible(value) {
				if (value) {
					if (this.skipNextInit) {
						this.skipNextInit = false;
						return;
					}
					this.initForm();
				}
			},
		},
		beforeUnmount() {
			revokeLocalImages(this.form.items);
		},
		methods: {

			getDefaultImageStyle() {
				return 'full';
			},
			resolveImageStyle(style) {
				if (this.hideImageStyle) {
					return 'full';
				}
				return style || this.getDefaultImageStyle();
			},
			initForm() {
				const source = normalizeHeaderImages(this.value);
				const incoming = normalizeLocalImages(source.items);
				const incomingIds = new Set(incoming.map((item) => item.id));
				this.form.items.forEach((img) => {
					if (!incomingIds.has(img.id)) {
						revokeLocalImageUrl(img.previewUrl);
					}
				});
				this.form = {
					imageStyle: this.resolveImageStyle(source.imageStyle),
					displayMode: source.displayMode,
					items: incoming,
				};
			},
			appendFiles(files) {
				const remain = MAX_LOCAL_IMAGES - this.form.items.length;
				if (remain <= 0) return;
				const validFiles = filterValidLocalFiles(files, remain);
				if (!validFiles.length) return;
				return this.uploadFiles(validFiles, null, { mode: 'append' });
			},
			createUploadPlaceholder(files = []) {
				return {
					id: `pending-${uuidv4()}`,
					filename: files.length > 1 ? `${files[0].name} 等${files.length}个` : (files[0]?.name || ''),
					previewUrl: '',
					uploading: true,
					local: true,
				};
			},
			isActiveUploadItem(img, index) {
				if (!this.isUploading || img.uploading !== true) {
					return false;
				}
				if (this.uploadMode === 'append') {
					return img.id === this.uploadTargetId;
				}
				if (this.uploadMode === 'replace') {
					return index === this.replaceUploadIndex;
				}
				return false;
			},
			removeUploadPlaceholder() {
				const index = this.form.items.findIndex((item) => item.id === this.uploadTargetId);
				if (index !== -1) {
					this.form.items.splice(index, 1);
				}
			},
			clearUploadState() {
				this.isUploading = false;
				this.uploadTasks = [];
				this.uploadMode = 'initial';
				this.uploadTargetId = '';
				this.replaceUploadIndex = -1;
			},
			async uploadFiles(files, onSuccess, { mode = 'initial', replaceIndex = -1 } = {}) {
				this.isUploading = true;
				this.uploadMode = mode;
				this.uploadTasks = createUploadTasks(files);

				if (mode === 'append') {
					const placeholder = this.createUploadPlaceholder(files);
					this.uploadTargetId = placeholder.id;
					this.form.items.push(placeholder);
				} else if (mode === 'replace') {
					this.replaceUploadIndex = replaceIndex;
					this.form.items[replaceIndex].uploading = true;
				}

				try {
					const items = await uploadHeaderMediaFiles(this, {
						type: 'image',
						files,
						onProgress: (payload) => updateUploadTaskProgress(this.uploadTasks, payload),
					});
					if (mode === 'append') {
						this.removeUploadPlaceholder();
						this.form.items.push(...items);
					} else if (mode === 'replace') {
						const old = this.form.items[replaceIndex];
						revokeLocalImageUrl(old?.previewUrl);
						this.form.items.splice(replaceIndex, 1, items[0]);
					} else {
						onSuccess?.(items);
					}
				} catch (error) {
					if (mode === 'append') {
						this.removeUploadPlaceholder();
					} else if (mode === 'replace' && this.form.items[replaceIndex]) {
						this.form.items[replaceIndex].uploading = false;
					}
					this.$message?.error?.('上传失败');
				} finally {
					this.clearUploadState();
				}
			},
			onAddImages() {
				this.filePickMode = 'add';
				this.$refs.fileInput.value = '';
				this.$refs.fileInput.click();
			},
			onReplaceImage(index) {
				this.replaceIndex = index;
				this.$refs.replaceInput.value = '';
				this.$refs.replaceInput.click();
			},
			onDeleteImage(index) {
				const target = this.form.items[index];
				if (target?.uploading) return;
				const [removed] = this.form.items.splice(index, 1);
				revokeLocalImageUrl(removed?.previewUrl);
			},
			onFileChange(event) {
				const files = Array.from(event.target.files || []);
				this.appendFiles(files);
				event.target.value = '';
			},
			onReplaceFileChange(event) {
				const file = (event.target.files || [])[0];
				event.target.value = '';
				if (!file || this.replaceIndex < 0) return;
				const valid = filterValidLocalFiles([file], 1);
				if (!valid.length) return;
				const replaceIndex = this.replaceIndex;
				this.replaceIndex = -1;
				this.uploadFiles(valid, null, { mode: 'replace', replaceIndex });
			},
			openWithFiles(files) {
				const validFiles = filterValidLocalFiles(files);
				if (!validFiles.length) return;
				revokeLocalImages(this.form.items);
				this.form = {
					imageStyle: this.getDefaultImageStyle(),
					displayMode: 'carousel',
					items: [],
				};
				this.skipNextInit = true;
				this.$emit('update:visible', true);
				this.uploadFiles(validFiles, (items) => {
					this.form.items = items;
				}, { mode: 'initial' });
			},
			openWithUploadedItems(images) {
				revokeLocalImages(this.form.items);
				this.form = {
					imageStyle: this.getDefaultImageStyle(),
					displayMode: 'carousel',
					items: normalizeLocalImages(images),
				};
				this.skipNextInit = true;
				this.$emit('update:visible', true);
			},
			onDragStart(id) {
				const target = this.form.items.find((item) => item.id === id);
				if (target?.uploading) return;
				this.dragId = id;
				this.dragOverId = '';
			},
			onItemDragOver(targetId, event) {
				event.preventDefault();
				if (!this.dragId || targetId === this.dragId) return;
				this.dragOverId = targetId;
			},
			onDragEnd() {
				if (this.dragOverId && this.dragOverId !== this.dragId) {
					const items = this.form.items;
					const fromIndex = items.findIndex((item) => item.id === this.dragId);
					const toIndex = items.findIndex((item) => item.id === this.dragOverId);
					if (fromIndex !== -1 && toIndex !== -1 && fromIndex !== toIndex) {
						const [moved] = items.splice(fromIndex, 1);
						items.splice(toIndex, 0, moved);
					}
				}
				this.dragId = '';
				this.dragOverId = '';
			},
			onSortDrop(event) {
				event.preventDefault();
				this.onDragEnd();
			},
			onCancel() {
				this.$emit('update:visible', false);
			},
			onConfirm() {
				if (!this.previewImages.length) {
					return;
				}
				this.$emit('confirm', normalizeHeaderImages({
					...this.form,
					imageStyle: this.resolveImageStyle(this.form.imageStyle),
					items: this.previewImages,
				}));
				this.$emit('update:visible', false);
			},
		},
	};
</script>

<style scoped lang="less">
	.g-image-settings {
		color: #333;
	}

	.g-image-settings__header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 20px;
	}

	.g-image-settings__title-row {
		display: flex;
		align-items: baseline;
		gap: 10px;
	}

	.g-image-settings__title {
		font-size: 18px;
		font-weight: 600;
		color: #333;
	}

	.g-image-settings__subtitle {
		font-size: 12px;
		color: #999;
	}

	.g-image-settings__close {
		font-size: 16px;
		color: #999;
		cursor: pointer;
		line-height: 1;

		&:hover {
			color: #666;
		}
	}

	.g-image-settings__body {
		display: flex;
		gap: 28px;
	}

	.g-image-settings__preview-col {
		flex-shrink: 0;
		width: 220px;
	}

	.g-image-settings__section-label {
		margin-bottom: 10px;
		font-size: 12px;
		color: #999;
	}

	.g-image-settings__phone {
		width: 200px;
		height: 360px;
		border: 1px solid #e8e8e8;
		border-radius: 16px;
		padding: 10px 8px;
		box-sizing: border-box;
		background: #fafafa;
	}

	.g-image-settings__phone-screen {
		height: 100%;
		border-radius: 10px;
		background: #fff;
		overflow: hidden;
		display: flex;
		flex-direction: column;

		&.is-style-full {
			:deep(.g-header-images-preview) {
				width: 100%;
			}
		}

		&.is-style-margin {
			:deep(.g-header-images-preview__tile.has-margin),
			:deep(.g-header-images-preview__carousel.has-margin) {
				padding: 0 10px;
			}
		}
	}

	.g-image-settings__phone-empty {
		flex: 1;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 12px;
		color: #ccc;
	}

	.g-image-settings__config-col {
		flex: 1;
		min-width: 0;
	}

	.g-image-settings__upload-list {
		display: flex;
		flex-wrap: wrap;
		gap: 10px;
		margin-bottom: 22px;
	}

	.g-image-settings__thumb {
		position: relative;
		width: 72px;
		height: 72px;
		border-radius: 4px;
		overflow: hidden;
		border: 1px solid #e8e8e8;
		cursor: grab;

		&.is-uploading {
			cursor: default;
			border-style: dashed;
			background: #fafafa;
		}

		img {
			width: 100%;
			height: 100%;
			object-fit: cover;
			display: block;
		}
	}

	.g-image-settings__thumb-actions {
		position: absolute;
		top: 2px;
		right: 2px;
		display: flex;
		gap: 2px;
	}

	.g-image-settings__thumb-btn {
		width: 16px;
		height: 16px;
		border-radius: 50%;
		background: rgba(0, 0, 0, 0.55);
		color: #fff;
		font-size: 10px;
		line-height: 16px;
		text-align: center;
		cursor: pointer;

		&.is-delete {
			font-size: 12px;
		}
	}

	.g-image-settings__upload-add {
		width: 72px;
		height: 72px;
		border: 1px dashed #d9d9d9;
		border-radius: 4px;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 6px;
		cursor: pointer;
		color: #999;
		background: #fafafa;
		transition: border-color 0.2s, color 0.2s;

		i {
			font-size: 20px;
			color: #bfbfbf;
		}

		span {
			font-size: 11px;
			line-height: 1;
		}

		&:hover {
			border-color: #285af9;
			color: #285af9;

			i {
				color: #285af9;
			}
		}
	}

	.g-image-settings__group {
		margin-bottom: 18px;
	}

	.g-image-settings__group-title {
		margin-bottom: 10px;
		font-size: 13px;
		color: #666;
	}

	.g-image-settings__radio-row {
		display: flex;
		align-items: center;
		gap: 18px;
		flex-wrap: wrap;
	}

	.g-image-settings__radio {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		font-size: 13px;
		color: #666;
		cursor: pointer;
		user-select: none;

		input {
			position: absolute;
			opacity: 0;
			pointer-events: none;
		}

		&.is-checked {
			color: #333;

			.g-image-settings__radio-dot {
				border-color: #285af9;

				&::after {
					opacity: 1;
				}
			}
		}
	}

	.g-image-settings__radio-dot {
		position: relative;
		width: 14px;
		height: 14px;
		border: 1px solid #d9d9d9;
		border-radius: 50%;
		box-sizing: border-box;
		display: flex;
		align-items: center;
		justify-content: center;
		&::after {
			content: '';
			width: 8px;
			height: 8px;
			border-radius: 50%;
			background: #285af9;
			opacity: 0;
		}
	}

	.g-image-settings__hint {
		margin: 8px 0 0;
		font-size: 12px;
		color: #bfbfbf;
		line-height: 1.5;
	}

	.g-image-settings__tips {
		margin-top: 8px;
		font-size: 12px;
		color: #bfbfbf;
		line-height: 1.8;

		p {
			margin: 0;
		}
	}

	.g-image-settings__footer {
		display: flex;
		align-items: center;
		gap: 12px;
		margin-top: 24px;
		padding-top: 16px;
		border-top: 1px solid #f0f0f0;
	}

	.g-image-settings__count {
		flex: 1;
		font-size: 13px;
		color: #999;
	}

	.g-image-settings__sort-hint {
		font-size: 12px;
		color: #bbb;
		flex-shrink: 0;
	}

	.g-image-settings__footer-actions {
		display: flex;
		gap: 10px;
	}

	.g-image-settings__btn {
		min-width: 72px;
		height: 32px;
		padding: 0 16px;
		border-radius: 4px;
		font-size: 14px;
		cursor: pointer;
		outline: none;

		&.is-cancel {
			border: 1px solid #d9d9d9;
			background: #fff;
			color: #333;

			&:hover {
				color: #5280ff;
    			border-color: #5280ff;
			}
		}

		&.is-confirm {
			border: none;
			background: #285af9;
			color: #fff;

			&:hover:not(:disabled) {
				background: #3868ff;
			}

			&:disabled {
				background: #3868ff;
				cursor: not-allowed;
			}
		}
	}

	.g-image-settings__file-input {
		display: none;
	}
</style>

<style lang="less">
	@import '../styles/edit-float-panel.less';

	.g-image-settings-modal {
		.edit-modal-scroll();

		.ant-modal-body {
			padding: 20px 24px 18px;
		}
	}
</style>
